/* eslint-disable @next/next/no-img-element */
"use client";

import { useCallback, useEffect, useState } from "react";

import Dropzone from "react-dropzone";
import { Image as ImageIcon } from "lucide-react";
import Loader from "./icons/Loader";
import { cn } from "@punchup/utils/cn";

type ImagePreviewType = File & { preview: string };

export const DragAndDropAreaImage = ({
  onReceivedImage,
  title,
  containerClassNames = "rounded-2xl justify-center items-center",
  isUploading = false,
  mini = false,
  showSelectedImage = true,
  disabled = false,
}: {
  onReceivedImage: (image: File) => void;
  title?: string;
  containerClassNames?: string;
  isUploading?: boolean;
  mini?: boolean;
  showSelectedImage?: boolean;
  disabled?: boolean;
}) => {
  const [files, setFiles] = useState<ImagePreviewType[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach((file) => {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, { preview: URL.createObjectURL(file) })
        )
      );
    });
    onReceivedImage(acceptedFiles[0]);
  }, []);

  const imgPreview = files.map((file) => (
    <div key={file.name}>
      <div>
        <img
          src={file.preview}
          alt={file.name as string}
          // Revoke data uri after image is loaded
          onLoad={() => {
            URL.revokeObjectURL(file.preview);
          }}
        />
        {isUploading && (
          <div
            className={cn(
              "flex justify-center items-center gap-x-1",
              mini && "text-xs"
            )}
          >
            <Loader width={10} /> Uploading Image...
          </div>
        )}
      </div>
    </div>
  ));

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    // https://react-dropzone.js.org/#!/Previews
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]);

  return (
    <>
      {!files.length || !showSelectedImage ? (
        <Dropzone
          onDrop={onDrop}
          accept={{ "image/*": [] }}
          maxFiles={1}
          disabled={disabled}
        >
          {({ getRootProps, getInputProps }) => (
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed border-gray-100 w-full h-full flex bg-gray-100",
                disabled ? "cursor-not-allowed opacity-50" : "cursor-copy",
                containerClassNames
              )}
            >
              <input {...getInputProps()} />
              <div
                className={cn(
                  "flex flex-col text-slate-gray-800 justify-center items-center w-full",
                  mini ? "px-1 gap-y-1" : "px-4 gap-y-2"
                )}
              >
                {!mini && title && <p className="pb-4">{title}</p>}
                <ImageIcon size={mini ? 12 : 24} />
                <p className={cn("text-center", mini && "text-xs")}>
                  Upload image
                </p>
              </div>
            </div>
          )}
        </Dropzone>
      ) : (
        imgPreview
      )}
    </>
  );
};
